import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TYPE_LABELS = { CUSTOMER_PASS_THROUGH: "Customer Pass-through", GROUP_OVERHEAD: "Group Overhead" };
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));

// Build 8 — Intercompany audit workspace (§37/§38): read-only, system-generated
// transactions with deterministic ids and both-sides drilldown. No manual entry.
export default function IntercompanyPage() {
  const { year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState("all");
  const [type, setType] = useState("all");
  const [counterparty, setCounterparty] = useState("all");
  const [txns, setTxns] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noGroup, setNoGroup] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/companies")
      .then(async (res) => {
        setCompanies(res.data);
        const hub = res.data.find((c) => c.is_group_company);
        if (!hub) { setNoGroup(true); return; }
        const yrs = await api.get(`/companies/${hub.id}/periods/years`);
        setYears(yrs.data);
        const pick = pickActiveYear(yrs.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!year) return;
    setLoading(true);
    const params = { year };
    if (month !== "all") params.month = Number(month);
    if (type !== "all") params.type = type;
    if (counterparty !== "all") params.counterparty = counterparty;
    api.get("/group/intercompany", { params })
      .then((res) => setTxns(res.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [year, month, type, counterparty]);

  const openDetail = async (t) => {
    try {
      const res = await api.get(`/group/intercompany/${t.id}`, { params: { year } });
      setDetail(res.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="intercompany-page">
      <PageHeader
        title="Intercompany"
        subtitle="System-generated transactions — read-only audit and drilldown"
        actions={
          <div className="flex items-center gap-2">
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="ic-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`ic-year-${y}`}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-32" data-testid="ic-month-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((label, i) => <SelectItem key={i + 1} value={String(i + 1)} data-testid={`ic-month-${i + 1}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-52" data-testid="ic-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="CUSTOMER_PASS_THROUGH" data-testid="ic-type-pt">Customer Pass-through</SelectItem>
                <SelectItem value="GROUP_OVERHEAD" data-testid="ic-type-oh">Group Overhead</SelectItem>
              </SelectContent>
            </Select>
            <Select value={counterparty} onValueChange={setCounterparty}>
              <SelectTrigger className="w-52" data-testid="ic-company-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`ic-company-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {noGroup && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <EmptyState title="No Group company configured" description="Mark a company as the Group company under Administration → Companies." testId="ic-no-group" />
        </div>
      )}
      {loading && <div className="py-16 text-center text-sm text-slate-400" data-testid="ic-loading">Loading…</div>}

      {!loading && txns && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ic-table">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                {["Month", "Type", "Source Company", "Target Company", "Source", "Amount", "Terms", "Cash Month", "VAT", "Transaction ID"].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-slate-100 hover:bg-accent/40 cursor-pointer"
                  onClick={() => openDetail(t)}
                  data-testid={`ic-row-${t.id}`}
                >
                  <td className="py-2.5 px-3 text-sm text-slate-700">{MONTHS[t.economic_month - 1]} {t.economic_year}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.type === "GROUP_OVERHEAD" ? "bg-[#EFF6FF] text-[#0047B3]" : "bg-[#F0FDFA] text-[#0F766E]"}`}>
                      {TYPE_LABELS[t.type]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-sm text-slate-700">{t.source_company_name}</td>
                  <td className="py-2.5 px-3 text-sm text-slate-700">{t.target_company_name}</td>
                  <td className="py-2.5 px-3 text-sm text-slate-600">{t.source_label}</td>
                  <td className="py-2.5 px-3 text-sm font-semibold text-slate-900">{fmtSEK(t.amount)} kr</td>
                  <td className="py-2.5 px-3 text-sm text-slate-600">{t.payment_terms_days != null ? `${t.payment_terms_days}d` : "—"}</td>
                  <td className="py-2.5 px-3 text-sm text-slate-600">{t.cash_month ? `${MONTHS[t.cash_month - 1]} ${t.cash_year}` : "—"}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-semibold ${t.vat_status === "CONFIGURED" ? "text-[#047857]" : "text-[#B45309]"}`} data-testid={`ic-vat-${t.id}`}>
                      {t.vat_status === "CONFIGURED" ? "Configured" : "Missing config"}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-[11px] font-mono text-slate-400">{t.id.slice(0, 28)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
          {txns.length === 0 && (
            <EmptyState title="No intercompany transactions" description="Transactions appear when operating companies deliver via the Group or overhead is allocated." testId="ic-empty" />
          )}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="ic-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{TYPE_LABELS[detail.type]} — {MONTHS[detail.economic_month - 1]} {detail.economic_year}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-xs text-slate-500">Origin</div>
                    <div className="font-semibold text-slate-900" data-testid="ic-detail-origin">{detail.source_label}</div>
                    <div className="text-xs text-slate-500">{detail.source_company_name}</div>
                  </div>
                  <div className="bg-secondary/50 rounded-md p-2.5">
                    <div className="text-xs text-slate-500">Payment terms / cash month</div>
                    <div className="font-semibold text-slate-900">
                      {detail.payment_terms_days != null ? `${detail.payment_terms_days} days` : "Not configured"}
                      {detail.cash_month ? ` → ${MONTHS[detail.cash_month - 1]} ${detail.cash_year}` : ""}
                    </div>
                    <div className="text-xs text-slate-500">VAT: {detail.vat_status === "CONFIGURED" ? "configured" : "missing configuration"}</div>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="ic-detail-sides">
                  {Object.entries(detail.sides).map(([k, v]) => (
                    <div key={k} className="flex justify-between px-3 py-2">
                      <span className="text-slate-600 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="font-semibold text-slate-900">{typeof v === "number" ? `${fmtSEK(v)}${k.includes("pct") ? "%" : " kr"}` : v}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] font-mono text-slate-400 break-all" data-testid="ic-detail-id">
                  Transaction ID: {detail.id} — one id links both legal-entity sides; consolidation (Build 9) eliminates by this id.
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
